// The skills library API (docs/12 "Workflow capabilities", D62): list (searchable), create,
// read, update, archive, and a skill's whole file set. Keys start with "skills".
import { queryOptions } from "@tanstack/react-query";

import { api, type Schemas } from "../api/client";
import { unwrap } from "../api/queries";

export type Skill = Schemas["SkillDto"];
export type SkillDetail = Schemas["SkillDetailDto"];
export type SkillFile = Schemas["SkillFileDto"];
export type SkillStatus = "active" | "archived";

export interface SkillMeta {
  slug: string;
  name: string;
  description: string;
}

/** A file as the editor holds it. */
export interface EditableFile {
  path: string;
  content: string;
}

const skillPath = (id: string) => ({ params: { path: { skill_id: id } } });

export const skillsQuery = (q = "") =>
  queryOptions({
    queryKey: ["skills", "list", q],
    queryFn: () => unwrap(api.GET("/api/admin/skills", { params: { query: q ? { q } : {} } })),
  });

export const skillQuery = (id: string) =>
  queryOptions({
    queryKey: ["skills", id],
    queryFn: () => unwrap(api.GET("/api/admin/skills/{skill_id}", skillPath(id))),
    staleTime: Infinity,
  });

export const createSkill = (meta: SkillMeta, files?: EditableFile[]): Promise<SkillDetail> =>
  unwrap(api.POST("/api/admin/skills", { body: { ...meta, files } }));

export const updateSkill = (id: string, meta: SkillMeta, status?: SkillStatus): Promise<Skill> =>
  unwrap(api.PUT("/api/admin/skills/{skill_id}", { ...skillPath(id), body: { ...meta, status } }));

export const archiveSkill = (id: string): Promise<Skill> => unwrap(api.DELETE("/api/admin/skills/{skill_id}", skillPath(id)));

export const replaceSkillFiles = (id: string, files: EditableFile[]): Promise<SkillFile[]> =>
  unwrap(api.PUT("/api/admin/skills/{skill_id}/files", { ...skillPath(id), body: { files } }));
